import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import PictionJS from 'piction-js';
import classNames from 'classnames';
import isArray from 'lodash/isArray';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import UMOGridItemOverlayMenu from './UMOGridItemOverlayMenu';
import { SearchParams, SearchTag } from '../../utils/searchUtils';
import { selectToggle as workspaceSelectToggle } from '../../actions/workspacePreview';
import {
  selectToggle as umoGallerySelectToggle,
  retrieveUMOs,
  setSelectedUMOIDs,
  setLastSelectedUMOID,
} from '../../actions/umoGallery';
import {
  toggleUMODetailView,
  toggleWorkspaceDetailView,
  setMetaUmoId,
  showMainDialog,
} from '../../actions/mainUI';
import { removeFromWorkspace } from '../../actions/umo';
import { moveUmoHandler } from '../../utils/umoUtils';
import { reportImage, showMetaEditTool } from '../../utils/uiUtils';
import appConfig from '../../appConfig';
import { MODAL_VIEW_TYPE_SHARE } from '../../constants/mainContentTypes';

const mapStateToProps = (state) => ({
  umoGallery: state.umoGallery,
});

function UMOGridItemOverlay({
  umo,
  onDetailViewClick,
  toggleFullImageView,
  isNotJustified,
  isWorkspaceItem,
  highlightSetID,
  isSmallGrid,
  isUMOGalleryWorkspace,
  isSelected,
  overlayClassModifier,
  dispatch,
  selectedWorkspaceData = { id: null },
  intl,
  isUnseenUpdate,
  galleryType,
  isRestricted,
  isOptionalIconsOverflow,
  umoGallery,
}) {
  /* eslint-disable no-underscore-dangle*/
  const isSmallIcons =
    isSmallGrid || (umo.data.w[0] < 200 && !isNotJustified) || isWorkspaceItem || isOptionalIconsOverflow;
  const zoomifyURL = umo.data && umo.data.z && umo.data.z[0];
  const onSelect = (e) => {
    if (isWorkspaceItem) {
      dispatch(workspaceSelectToggle(umo.id));
    } else {
      dispatch(umoGallerySelectToggle(umo.id));
      dispatch(setLastSelectedUMOID(umo.id));
    }
    e.stopPropagation();
  };

  const onViewDetailClick = (item) => {
    if (!item.shiftKey || !item.ctrlKey) {
      if (isUMOGalleryWorkspace && !appConfig.removeWorkspaceCardView) {
        dispatch(toggleWorkspaceDetailView(umo.id, selectedWorkspaceData));
      } else if (onDetailViewClick) {
        onDetailViewClick(umo.id);
      } else {
        dispatch(toggleUMODetailView(umo.id));
      }
    }
  };

  /**
   *
   * @param {string} value it is the index value of new selected item
   * @returns it returns the appended array of already selected item if any and the new selected indexes
   */
  const getNextValue = (value) => {
    const hasBeenSelected = !umoGallery.get('selectedUMOIDs').includes(value);
    const newSelectedItems = getNewSelectedItems(value);
    const selections = [
      ...new Set([...umoGallery.get('selectedUMOIDs'), ...newSelectedItems]),
    ];

    if (!hasBeenSelected) {
      return selections.filter((item) => !newSelectedItems.includes(item));
    }
    dispatch(setLastSelectedUMOID(umo.id));
    return selections;
  };

  /**
   *
   * @param {string} value It is the index of new selected item
   * @returns It returns the list of umo ids between last selected umo id and the new selected umo ID
   */
  const getNewSelectedItems = (value) => {
    const items = umoGallery.get('umoList').toJS();
    const currentSelectedIndex = items.findIndex(
      (item) => item.id[0] === value
    );
    const lastSelectedIndex = items.findIndex(
      (item) => item.id[0] === umoGallery.get('lastSelectedUMOID')
    );
    return items
      .slice(
        Math.min(lastSelectedIndex, currentSelectedIndex),
        Math.max(lastSelectedIndex, currentSelectedIndex) + 1
      )
      .map((item) => item.id[0]);
  };

  const onOverlayClick = (e) => {
    if (e.ctrlKey) {
      if (isWorkspaceItem) {
        dispatch(workspaceSelectToggle(umo.id));
      } else {
        dispatch(umoGallerySelectToggle(umo.id));
        dispatch(setLastSelectedUMOID(umo.id));
      }
    } else if (e.shiftKey) {
      if (isWorkspaceItem) {
        dispatch(workspaceSelectToggle(umo.id));
      } else {
        if (
          umoGallery.get('lastSelectedUMOID') == null ||
          umoGallery.get('lastSelectedUMOID') == umo.id
        ) {
          dispatch(umoGallerySelectToggle(umo.id));
          dispatch(setLastSelectedUMOID(umo.id));
        }
        if (umoGallery.get('selectedUMOIDs').toJS().length > 0)
          dispatch(setSelectedUMOIDs(getNextValue(umo.id)));
      }
    } else {
      // dispatch(setMetaUmoId(umo.id)); // Disabled one click trigger for meta view and moved to view meta data icon
      onViewDetailClick(e);
    }
    e.stopPropagation();
  };

  const onWorkspaceAddClick = (item) => {
    if (!item.shiftKey || !item.ctrlKey) {
      if (selectedWorkspaceData.id) {
        const origin = {
          id: 1,
          isWorkspace: false,
        };
        const target = {
          id: selectedWorkspaceData.id,
          isWorkspace: true,
        };
        moveUmoHandler(origin, target, [umo.id], dispatch);
      }
    }
    item.stopPropagation();
  };

  const onWorkspaceRemoveClick = (e) => {
    if (highlightSetID || selectedWorkspaceData.id) {
      dispatch(
        removeFromWorkspace(highlightSetID || selectedWorkspaceData.id, [umo])
      );
    }
    e.stopPropagation();
  };

  const retrieveRelatedUMOs = (umoID) => {
    dispatch(
      retrieveUMOs(
        new SearchParams({
          tagList: [
            new SearchTag(
              'search',
              'RELATED_IMAGES',
              umoID,
              <FormattedMessage id="umoGridItem.relatedItemTagLabel" />,
              <FormattedMessage id="umoGridItem.relatedItemTagDescription" />
            ),
          ],
        })
      )
    );
  };

  const onReportImage = (e) => {
    e.stopPropagation();
    reportImage(dispatch, umo.id, umo.name, intl.locale);
  };
  const onMetaViewClick = (e) => {
    e.stopPropagation();
    dispatch(setMetaUmoId(umo.id));
  };

  const showFullImage = (e) => {
    e.stopPropagation();
    const umoData = {
      umo,
      fullSrc:
        umo.type === 'PHOTO'
          ? umo.webQuality.url
          : umo.originalList.all[0]._data.$.h,
      type: umo.type,
      zoomifyURL,
    };
    toggleFullImageView(true, umoData);
  };

  const workspaceAddTooltip = (
    <Tooltip id="workspace-add-tooltip">
      {selectedWorkspaceData.id
        ? intl.formatMessage(
            { id: 'umoGridItem.addToWorkspaceTooltip' },
            { workspaceName: selectedWorkspaceData.name }
          )
        : intl.formatMessage({ id: 'umoGridItem.selectWorkspaceTooltip' })}
    </Tooltip>
  );

  const reportImageTooltip = (
    <Tooltip id="workspace-add-tooltip">
      {intl.formatMessage({ id: 'umoGridItem.reportImageTooltip' })}
    </Tooltip>
  );

  const shareImageTooltip = (
    <Tooltip id="workspace-add-tooltip">
      {intl.formatMessage({ id: 'umoGridItem.shareImageTooltip' })}
    </Tooltip>
  );

  const metatags = isArray(appConfig.customTooltipMetatag)
    ? appConfig.customTooltipMetatag
    : [appConfig.customTooltipMetatag];

  const customTags = metatags
    .map((tag) => umo.metadataList?.getMetadata(tag || '')?.value)
    ?.filter(Boolean);

  const viewMetaDataToolTip = (
    <Tooltip
      id="workspace-add-tooltip"
      className="umo-grid-item__metadata-tooltip"
    >
      {customTags.length > 0 ? (
        <ul style={{ margin: '0' }}>
          {customTags.map((tag) => (
            <>
              <li style={{ textAlign: 'left' }} key={tag}>
                {tag?.length > 500 ? tag?.substring(0, 500) + ' ...' : tag}
              </li>
              <br />
            </>
          ))}
        </ul>
      ) : (
        umo.name
      )}
    </Tooltip>
  );

  const addToWorkspaceButton = appConfig.removeWorkspaces ? null : (
    <OverlayTrigger placement="bottom" overlay={workspaceAddTooltip}>
      <button
        onClick={onWorkspaceAddClick}
        className={classNames(
          'umo-grid-item__overlay-more-add',
          isSmallIcons ? 'sm-overlay' : ''
        )}
      >
        <i className="umo-grid-item__overlay-more-add-icon" />
      </button>
    </OverlayTrigger>
  );
  const getShareLinks = (e) => {
    e.stopPropagation();
    dispatch(showMainDialog(MODAL_VIEW_TYPE_SHARE, { umoID: umo.id }));
  };
  const shareLinkBtn = (
    <OverlayTrigger placement="bottom" overlay={shareImageTooltip}>
      <button
        id="share-button"
        className={classNames(
          'umo-grid-item__overlay-share-image',
          appConfig.reportImage ? '' : 'no-report',
          isSmallIcons ? 'sm-overlay' : '',
          isWorkspaceItem ? 'pinned' : ''
        )}
        onClick={getShareLinks}
      >
        <i className="umo-grid-item__overlay-share-image-button" />
      </button>
    </OverlayTrigger>
  );
  // Allow fullscreen view for photos (web quality) or documents/records (html view)
  const fullImageType =
    (umo.type === 'PHOTO' && umo.webQuality) ||
    (!['PHOTO', 'AUDIO', 'VIDEO'].includes(umo.type) &&
      umo.originalList.all[0] &&
      umo.originalList.all[0]._data.$.h);

  const zoomifyBtn =
    !isRestricted && fullImageType && !appConfig.disableZoomifyIcon ? (
      <button
        id="zoomify-button"
        onClick={showFullImage}
        className={classNames(
          'umo-grid-item__overlay-fullscreen',
          isSmallIcons ? 'sm-overlay' : '',
          isWorkspaceItem ? 'pinned' : ''
        )}
      >
        <i className="umo-grid-item__overlay-fullscreen-button" />
      </button>
    ) : null;

  const onMetaEditClick = (e) => {
    e.stopPropagation();
    showMetaEditTool(umo, dispatch);
  };

  return (
    <div
      className={classNames('umo-grid-item__overlay', overlayClassModifier, {
        'umo-grid-item__overlay--noj': isNotJustified,
        'umo-grid-item__overlay--sm': isSmallGrid,
      })}
    >
      {isUnseenUpdate ? (
        <span
          className={classNames('umo-grid-item__overlay-updated', {
            noj: isNotJustified,
            sm: isSmallIcons,
          })}
        />
      ) : null}
      <div
        id={`scrollid-${umo.id}`}
        onClick={onOverlayClick}
        className="umo-grid-item__overlay-tools"
      >
        <button
          onClick={onSelect}
          className={classNames(
            'umo-grid-item__overlay-select',
            isSmallIcons ? 'sm-overlay' : '',
            isWorkspaceItem ? 'pinned' : ''
          )}
        >
          <i
            className={classNames(
              isSelected
                ? 'umo-grid-item__overlay-select-icon--checked'
                : 'umo-grid-item__overlay-select-icon'
            )}
          />
        </button>
        <div
          className={classNames(
            'umo-grid-item__overlay-left-icons-container',
            isSmallIcons ? 'umo-grid-item__overlay-sm-version' : ''
          )}
        >
          {galleryType === SearchParams.searchTypes.WORKSPACE ||
          isWorkspaceItem ||
          isUMOGalleryWorkspace ||
          galleryType === SearchParams.searchTypes.HIGHLIGHT ? (
            <button
              onClick={onWorkspaceRemoveClick}
              className={classNames(
                'umo-grid-item__overlay-more-remove',
                isSmallIcons ? 'sm-overlay' : '',
                isWorkspaceItem ? 'pinned' : ''
              )}
            >
              <i className="umo-grid-item__overlay-more-remove-icon" />
            </button>
          ) : (
            addToWorkspaceButton
          )}

          {appConfig.reportImage ? (
            <OverlayTrigger placement="bottom" overlay={reportImageTooltip}>
              <button
                id="report-button"
                className={classNames(
                  'umo-grid-item__overlay-report-image',
                  isSmallIcons ? 'sm-overlay' : '',
                  isWorkspaceItem ? 'pinned' : ''
                )}
                onClick={onReportImage}
              >
                <i className="umo-grid-item__overlay-report-image-button" />
              </button>
            </OverlayTrigger>
          ) : null}
          {appConfig.enableLinkShare && !isRestricted ? shareLinkBtn : null}
          {
            <OverlayTrigger placement="bottom" overlay={viewMetaDataToolTip}>
              <button
                id="view-meta-button"
                className={classNames(
                  'umo-grid-item__overlay-info',
                  isSmallIcons ? 'sm-overlay' : '',
                  isWorkspaceItem ? 'pinned' : ''
                )}
                onClick={onMetaViewClick}
              >
                <i className="umo-grid-item__overlay-info-button" />
              </button>
            </OverlayTrigger>
          }
          {zoomifyBtn}
          {appConfig.enableMetaEdit && (
            <button
              className={classNames(
                'umo-grid-item__overlay-editmeta',
                isSmallIcons ? 'sm-overlay' : ''
              )}
              onClick={onMetaEditClick}
            >
              <i className="umo-grid-item__overlay-editmeta-button" />
            </button>
          )}
        </div>
        <div
          className={classNames(
            'umo-grid-item__overlay-right-icons-container',
            isSmallIcons ? 'umo-grid-item__overlay-sm-version' : ''
          )}
        >
          {isWorkspaceItem ? (
            ''
          ) : (
            <div
              className={classNames(
                'umo-grid-item__overlay-more-images',
                isSmallIcons ? 'sm-overlay' : '',
                isWorkspaceItem ? 'pinned' : ''
              )}
            >
              <UMOGridItemOverlayMenu
                umo={umo}
                retrieveRelatedUMOs={retrieveRelatedUMOs}
                classModifier={isSmallIcons ? 'sm-overlay' : ''}
                isRestricted={isRestricted}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

UMOGridItemOverlay.propTypes = {
  onDetailViewClick: PropTypes.func,
  toggleFullImageView: PropTypes.func,
  umo: PropTypes.instanceOf(PictionJS.UMO),
  isNotJustified: PropTypes.bool,
  isWorkspaceItem: PropTypes.bool,
  highlightSetID: PropTypes.string,
  isSmallGrid: PropTypes.bool,
  isUMOGalleryWorkspace: PropTypes.bool,
  isSelected: PropTypes.bool,
  overlayClassModifier: PropTypes.string,
  dispatch: PropTypes.func,
  selectedWorkspaceData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  isUnseenUpdate: PropTypes.bool,
  galleryType: PropTypes.string,
  isRestricted: PropTypes.bool,
  isOptionalIconsOverflow: PropTypes.bool,
};

export default connect(mapStateToProps)(injectIntl(UMOGridItemOverlay));
